import Glyph from "@/components/Glyph/Glyph"
import TemplateTile from "@/components/TemplateTile/TemplateTile"
import { CUSTOM_BLURB, CUSTOM_ICON, TEMPLATES } from "@/content/templates"
import type { SessionTemplate } from "@/content/templates"
import { RULES_COPY } from "@/content/sessionRules"
import styles from "@/styles/components/SessionRules.module.css"

/*
 * The four tiles. Three presets and Custom, which has no payload — it is where
 * the config already is once no preset matches, so its press opens the rules
 * that got it there rather than writing anything.
 */
interface TemplatePickerProps {
  /** The preset in effect, or null for Custom. */
  active: SessionTemplate | null
  plannedMinutes: number
  /**
   * Which preset's write is still out, if any.
   *
   * A template rewrites every round's length and every exercise flag in one
   * patch, and the tile it was pressed on lights up from the config the listener
   * has already applied — so the wait is invisible without this. Custom writes
   * nothing, so it never has one.
   */
  applying: string | null
  onPick: (template: SessionTemplate) => void
  onPickCustom: () => void
}

export default function TemplatePicker({
  active,
  plannedMinutes,
  applying,
  onPick,
  onPickCustom,
}: TemplatePickerProps) {
  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <span className={styles.label}>{RULES_COPY.template}</span>
        <span className={styles.meta}>{RULES_COPY.sessionLength(plannedMinutes)}</span>
      </div>

      <div className={styles.templates}>
        {TEMPLATES.map((template) => (
          <TemplateTile
            key={template.key}
            label={template.name}
            icon={<Glyph icon={template.icon} />}
            selected={active?.key === template.key}
            /* One patch each, and the tile lights up from the config the
             * listener has already applied — so a second preset pressed while
             * the first is out would leave a config that is neither of them. */
            loading={applying === template.key}
            disabled={applying !== null && applying !== template.key}
            onSelect={() => onPick(template)}
          />
        ))}
        <TemplateTile
          label="Custom"
          icon={<Glyph icon={CUSTOM_ICON} />}
          selected={active === null}
          disabled={applying !== null}
          onSelect={onPickCustom}
        />
      </div>

      <p className={styles.blurb}>{active?.blurb ?? CUSTOM_BLURB}</p>
    </section>
  )
}
